// Copyright (C) 2022 Intel Corporation
//
// SPDX-License-Identifier: MIT

import { clamp } from 'utils/math';
import { TrackerModel, TrackingResult } from './opencv-interfaces';

export type TrackerNano = TrackerModel;

export default class TrackerNanoImplementation implements TrackerNano {
    public name: string;
    private imageData: ImageData | null;
    private cv: any;
    private trackerNano: any;

    constructor(cv: any) {
        this.cv = cv;
        this.trackerNano = new cv.TrackerNano();
        this.imageData = null;
        this.name = 'TrackerNano';
    }

    public init(src: ImageData, points: number[]): void {
        console.log('Init...');

        if (points.length !== 4) {
            throw Error(`TrackerNano must be initialized with rectangle, but got ${points.length % 2} points.`);
        }
        this.imageData = src;

        // cut bounding box if its out of image bounds
        const x1 = clamp(points[0], 0, src.width);
        const y1 = clamp(points[1], 0, src.height);
        const x2 = clamp(points[2], 0, src.width);
        const y2 = clamp(points[3], 0, src.height);

        const [width, height] = [x2 - x1, y2 - y1];
        if (width === 0 || height === 0) {
            throw Error('TrackerNano got rectangle out of image bounds');
        }

        let matImageRGBA = null;
        const matImageRGB = new this.cv.Mat();
        try {
            matImageRGBA = this.cv.matFromImageData(src);
            const rect = new this.cv.Rect(x1, y1, width, height);
            this.cv.cvtColor(matImageRGBA, matImageRGB, this.cv.COLOR_RGBA2RGB);
            this.trackerNano.init(matImageRGB, rect);
        } finally {
            if (matImageRGBA) matImageRGBA.delete();
            if (matImageRGB) matImageRGB.delete();
        }
    }

    public reinit(points: number[]): void {
        if (!this.imageData) {
            throw Error('TrackerNano needs to be initialized before re-initialization');
        }
        this.init(this.imageData, points);
    }

    public update(src: ImageData): TrackingResult {
        this.imageData = src;
        let matImageRGBA = null;
        const matImageRGB = new this.cv.Mat();
        try {
            matImageRGBA = this.cv.matFromImageData(src);
            this.cv.cvtColor(matImageRGBA, matImageRGB, this.cv.COLOR_RGBA2RGB);
            const [updated, rect] = this.trackerNano.update(matImageRGB);
            return { updated, points: [rect.x, rect.y, rect.x + rect.width, rect.y + rect.height] };
        } finally {
            if (matImageRGBA) matImageRGBA.delete();
            if (matImageRGB) matImageRGB.delete();
        }
    }

    public delete(): void {
        this.trackerNano.delete();
    }
}
